import { AdditionalCostType, Offer, TransportMode } from './proposal.types';

export type OfferAdditionalCostForPricing = { value: number; type: AdditionalCostType };
export type OfferForPricing = { basePrice: number; additionalCosts?: OfferAdditionalCostForPricing[] };
export type { TransportMode };

// -------------------------------------------------------------------------------------------------
// Freight per m3 (CBM). Mirror of the backend pure functions (src/proposals/pricing.ts in the
// backend repo). The backend is the single authority for capacity, volume, freight per m3, line
// total and Proposal.total (PRD D1); these copies are for PREVIEWS only and their results are
// never sent to the backend for container lines.
// -------------------------------------------------------------------------------------------------

export interface CapacityRow {
  specyId: number;
  dryTypeId: number;
  dryTypeName: string;
  quantityTypeId: number;
  capacityM3: number;
}

export interface CapacityQuery {
  specyId: number;
  parentSpecyId?: number | null;
  dryTypeId: number;
  dryTypeName: string; // 'AD' | 'KD' | 'AD_KD'
  quantityType: { id: number; quantityInM3: number };
}

export interface ResolvedCapacity {
  capacityM3: number;
  exceedsPhysicalVolume: boolean;
  derived: boolean;
  via: 'specy' | 'parent'; // which species answered
  rule: 'exact' | 'min' | 'single'; // exact row | AD_KD = min(AD, KD) | AD_KD with only one of AD/KD
}

export interface ContainerLineInput {
  quantityTypeId: number;
  count: number;
  typeName?: string;
}

export interface SnapshotContainer {
  quantityTypeId: number;
  count: number;
  capacityM3?: number;
}

export interface FreightBasis {
  quantityTypeId: number;
  basisAmount: number;
}

export interface LineForTotal {
  basePrice: number;
  additionalCosts?: OfferAdditionalCostForPricing[];
  volumeM3: number;
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function sumAdditionalCosts(costs: OfferAdditionalCostForPricing[]): number {
  return costs.reduce((sum, cost) => {
    if (typeof cost.value !== 'number' || Number.isNaN(cost.value)) {
      throw new Error('Custo adicional invalido: valor tem de ser numerico.');
    }
    if (cost.value < 0) {
      throw new Error('Custo adicional invalido: valor nao pode ser negativo.');
    }
    return sum + cost.value;
  }, 0);
}

function assertIncludedCostTypesIsArray(
  includedCostTypes: unknown,
): asserts includedCostTypes is AdditionalCostType[] {
  if (!Array.isArray(includedCostTypes)) {
    throw new Error('Incoterm invalido: includedCostTypes tem de ser um array.');
  }
}

/**
 * Resolves how many m3 of a species (in a dry state) fit in one container of a type.
 * Scopes are tried in order: the species, then its single-level parent (PRD D5, deviation D-21).
 * Inside a scope: AD_KD = min(AD, KD), or the only one that exists (PRD D4); any other dry type
 * needs an exact row. Rows of another container type never answer.
 */
export function resolveCapacityM3(capacities: CapacityRow[], query: CapacityQuery): ResolvedCapacity | null {
  const scopes: { specyId: number; via: 'specy' | 'parent' }[] = [{ specyId: query.specyId, via: 'specy' }];
  if (query.parentSpecyId != null) {
    scopes.push({ specyId: query.parentSpecyId, via: 'parent' });
  }
  const physicalVolume = query.quantityType.quantityInM3;

  for (const scope of scopes) {
    const rows = capacities.filter(
      (row) =>
        row.specyId === scope.specyId &&
        row.quantityTypeId === query.quantityType.id &&
        Number.isFinite(row.capacityM3) &&
        row.capacityM3 > 0,
    );

    if (query.dryTypeName === 'AD_KD') {
      const found = [rows.find((r) => r.dryTypeName === 'AD'), rows.find((r) => r.dryTypeName === 'KD')].filter(
        (row): row is CapacityRow => !!row,
      );
      if (found.length === 0) {
        continue;
      }
      const capacityM3 = Math.min(...found.map((row) => row.capacityM3));
      return {
        capacityM3,
        exceedsPhysicalVolume: capacityM3 > physicalVolume,
        derived: true,
        via: scope.via,
        rule: found.length === 2 ? 'min' : 'single',
      };
    }

    const exact = rows.find((row) => row.dryTypeId === query.dryTypeId);
    if (exact) {
      return {
        capacityM3: exact.capacityM3,
        exceedsPhysicalVolume: exact.capacityM3 > physicalVolume,
        derived: false,
        via: scope.via,
        rule: 'exact',
      };
    }
  }

  return null;
}

/** Offer volume = sum of count x capacity. Zero count lines are ignored; a missing capacity throws. */
export function offerVolumeM3(
  lines: ContainerLineInput[],
  resolveCapacity: (quantityTypeId: number) => { capacityM3: number } | null,
): number {
  for (const line of lines) {
    if (
      typeof line.count !== 'number' ||
      !Number.isFinite(line.count) ||
      !Number.isInteger(line.count) ||
      line.count < 0
    ) {
      throw new Error('Numero de contentores invalido.');
    }
  }
  let sum = 0;
  for (const line of lines) {
    if (line.count === 0) {
      continue;
    }
    const capacity = resolveCapacity(line.quantityTypeId);
    if (capacity == null) {
      throw new Error(`Capacidade de contentor em falta para ${line.typeName ?? line.quantityTypeId}.`);
    }
    sum += line.count * capacity.capacityM3;
  }
  // 1e-6 rounding kills float noise such as 3 x 33.2 = 99.60000000000001
  return round6(sum);
}

/** Volume of a non container offer: quantity x physical volume of its quantity type (m3 type = 1). */
export function legacyVolumeM3(
  quantity:
    | { quantity?: number | null; quantityType?: { quantityInM3?: number | null } | null }
    | null
    | undefined,
): number {
  const volume = (quantity?.quantity ?? 0) * (quantity?.quantityType?.quantityInM3 ?? 0);
  return Number.isFinite(volume) && volume > 0 ? volume : 0;
}

// -------------------------------------------------------------------------------------------------
// D19: requested volume drives the container count. Mirror of the backend functions of the same name.
// -------------------------------------------------------------------------------------------------

/** Rounds to 6 decimals: float noise such as 84 - 83.59 = 0.4099999999999966 reads 0.41. Same rule as the backend. */
export function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** A ratio this close to an integer IS that integer (56.00000000000001 / 28 is 2, not 3). */
export const CONTAINERS_NEEDED_EPSILON = 1e-9;

/** True for a finite number > 0: the only requested volume that drives the billed volume. */
function isUsableVolume(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Containers of ONE type needed for a requested volume: ceil(requested / capacity) with a float-noise guard.
 * Throws 'Volume invalido.' / 'Capacidade de contentor invalida.' (Portuguese ASCII, like the backend). Result >= 1.
 */
export function containersNeeded(requestedVolumeM3: number, capacityM3: number): number {
  if (!isUsableVolume(requestedVolumeM3)) {
    throw new Error('Volume invalido.');
  }
  if (!isUsableVolume(capacityM3)) {
    throw new Error('Capacidade de contentor invalida.');
  }
  const ratio = requestedVolumeM3 / capacityM3;
  const nearest = Math.round(ratio);
  if (nearest >= 1 && Math.abs(ratio - nearest) < CONTAINERS_NEEDED_EPSILON) {
    return nearest;
  }
  return Math.max(1, Math.ceil(ratio));
}

export interface CapacityRowForVolume {
  count: number;
  capacityM3: number;
}

function sumCapacity(rows: CapacityRowForVolume[]): number {
  return rows.reduce(
    (sum, row) =>
      Number.isFinite(row.count) && row.count > 0 && Number.isFinite(row.capacityM3)
        ? sum + row.count * row.capacityM3
        : sum,
    0,
  );
}

/**
 * Volume the client is billed for (Option 1): the requested volume when it is a finite number > 0, otherwise the sum
 * of count x capacity. Never throws.
 */
export function billedVolumeM3(requestedVolumeM3: number | null | undefined, rows: CapacityRowForVolume[]): number {
  if (isUsableVolume(requestedVolumeM3)) {
    return round6(requestedVolumeM3);
  }
  return round6(sumCapacity(rows));
}

/** Whole-container capacity not covered by the requested volume: max(0, round6(sum - requested)); 0 without a usable requested volume. */
export function idleCapacityM3(requestedVolumeM3: number | null | undefined, rows: CapacityRowForVolume[]): number {
  if (!isUsableVolume(requestedVolumeM3)) {
    return 0;
  }
  return Math.max(0, round6(sumCapacity(rows) - requestedVolumeM3));
}

/**
 * Volume of a proposal line that has a container snapshot. An unresolved capacity prices 0 (pending snapshot); a usable
 * requested volume is the billed volume; otherwise the count x capacity path (which keeps the bad count throw).
 */
export function lineSnapshotVolumeM3(
  rows: SnapshotContainer[],
  unresolved: string[],
  requestedVolumeM3?: number | null,
): number {
  if (unresolved.length > 0) {
    return 0;
  }
  if (isUsableVolume(requestedVolumeM3)) {
    return billedVolumeM3(
      requestedVolumeM3,
      rows.map((row) => ({ count: row.count, capacityM3: row.capacityM3 ?? 0 })),
    );
  }
  return offerVolumeM3(
    rows.map((row) => ({ quantityTypeId: row.quantityTypeId, count: row.count })),
    (quantityTypeId) => {
      const row = rows.find((candidate) => candidate.quantityTypeId === quantityTypeId);
      return row ? { capacityM3: row.capacityM3 ?? 0 } : null;
    },
  );
}

/**
 * Freight per m3 contributed by each container type: basisAmount x count / volume. Only types that
 * have a basis appear in the result (a type without freight contributes nothing).
 */
export function freightValuesPerM3(
  bases: FreightBasis[],
  snapshot: SnapshotContainer[],
  volumeM3: number,
): Record<number, number> {
  if (typeof volumeM3 !== 'number' || !Number.isFinite(volumeM3) || volumeM3 <= 0) {
    throw new Error('Capacidade de contentor em falta para calcular o frete.');
  }
  for (const basis of bases) {
    if (typeof basis.basisAmount !== 'number' || Number.isNaN(basis.basisAmount)) {
      throw new Error('Custo adicional invalido: valor tem de ser numerico.');
    }
    if (basis.basisAmount < 0) {
      throw new Error('Custo adicional invalido: valor nao pode ser negativo.');
    }
  }
  const seen = new Set<number>();
  for (const basis of bases) {
    if (seen.has(basis.quantityTypeId)) {
      throw new Error('Ja existe frete para este tipo de contentor nesta linha.');
    }
    seen.add(basis.quantityTypeId);
  }

  const result: Record<number, number> = {};
  for (const basis of bases) {
    const container = snapshot.find((c) => c.quantityTypeId === basis.quantityTypeId && c.count > 0);
    if (!container) {
      throw new Error('Frete nao corresponde a nenhum tipo de contentor da linha.');
    }
    result[basis.quantityTypeId] = (basis.basisAmount * container.count) / volumeM3;
  }
  return result;
}

/** True when every container type of the snapshot (count > 0) has a freight basis. */
export function isFreightComplete(bases: FreightBasis[], snapshot: SnapshotContainer[]): boolean {
  return snapshot
    .filter((container) => container.count > 0)
    .every((container) => bases.some((basis) => basis.quantityTypeId === container.quantityTypeId));
}

/**
 * Unit price per m3: base price + included costs, rounded to cents (always), then rounded UP to
 * the Incoterm multiple when one is configured.
 */
export function calculateOfferUnitPrice(
  offer: OfferForPricing,
  includedCostTypes: AdditionalCostType[],
  roundToMultiple: number | null,
): number {
  assertIncludedCostTypesIsArray(includedCostTypes);
  const included = (offer.additionalCosts ?? []).filter((cost) => includedCostTypes.includes(cost.type));
  const cents = round2(offer.basePrice + sumAdditionalCosts(included));
  if (roundToMultiple != null && roundToMultiple > 0) {
    return Math.ceil(cents / roundToMultiple) * roundToMultiple;
  }
  return cents;
}

/** @deprecated use calculateOfferUnitPrice; kept so the existing callers and tests stay valid. */
export const calculateOfferTotal = calculateOfferUnitPrice;

/** Line total = round2(unit price x volume). Volume 0, negative or NaN gives 0. */
export function calculateLineTotal(unitPrice: number, volumeM3: number): number {
  if (!Number.isFinite(volumeM3) || volumeM3 <= 0) {
    return 0;
  }
  return round2(unitPrice * volumeM3);
}

/** Proposal total = sum of line totals (each unit price rounded before multiplying by its volume). */
export function calculateProposalTotal(
  lines: LineForTotal[],
  includedCostTypes: AdditionalCostType[],
  roundToMultiple: number | null,
): number {
  assertIncludedCostTypesIsArray(includedCostTypes);
  const sum = lines.reduce(
    (acc, line) =>
      acc + calculateLineTotal(calculateOfferUnitPrice(line, includedCostTypes, roundToMultiple), line.volumeM3),
    0,
  );
  return round2(sum);
}

export interface FreightOption {
  transportMode: TransportMode;
}

export function filterFreightByMode<T extends FreightOption>(freightOptions: T[], mode: TransportMode): T[] {
  return freightOptions.filter((option) => option.transportMode === mode);
}

export interface ImportTaxCostInput {
  appliesImportTax: boolean;
  includedCostTypes: AdditionalCostType[];
  offerBasePrice: number;
  containerVolumeM3: number;
  exchangeRate: number;
  fixedCustomsFee: number;
  customsPercentage: number;
}

export function suggestImportTaxCost(input: ImportTaxCostInput): number | null {
  if (!input.appliesImportTax) {
    return null;
  }
  if (!input.includedCostTypes.includes('DESALFANDEGAMENTO')) {
    return null;
  }
  if (!Number.isFinite(input.containerVolumeM3) || input.containerVolumeM3 <= 0) {
    return null;
  }

  const tax = input.offerBasePrice * input.customsPercentage;
  const customsFeeConverted = (input.fixedCustomsFee / input.containerVolumeM3) * input.exchangeRate;
  const total = tax + customsFeeConverted;

  return Math.round(total * 100) / 100;
}

// TODO(OQ1): Settings.insurancePercentage 0.15 is probably the margin (the client's sheet uses 1.15
// as margin and the meeting used a flat 5 EUR/m3 insurance). See PRD open question OQ1; do not
// change this without confirming with Rui.
export function suggestInsuranceCost(offerBasePrice: number, insurancePercentage: number): number {
  return Math.round(offerBasePrice * insurancePercentage * 100) / 100;
}

export function suggestTransportCost(
  fixedTransportFee: number,
  containerVolumeM3: number,
  exchangeRate: number,
): number | null {
  if (!Number.isFinite(containerVolumeM3) || containerVolumeM3 <= 0) {
    return null;
  }
  const total = (fixedTransportFee / containerVolumeM3) * exchangeRate;
  return Math.round(total * 100) / 100;
}
